import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { Apple, Smartphone, Download, Share, Home, Menu, QrCode } from "lucide-react";

// Public production URL — the manifest lives here, so scanning this from a
// phone triggers the PWA install prompt on Android and the Add-to-Home-Screen
// flow on iOS Safari.
const INSTALL_URL = "https://utilitymap.org";

/**
 * "Get the app" section — presents iPhone + Android install buttons.
 * Uses the beforeinstallprompt event when available (Android Chrome / Edge),
 * otherwise opens a modal with the correct home-screen install instructions.
 */
const GetTheApp = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [modal, setModal] = useState(null); // "ios" | "android" | null

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    // Detect if already running as an installed PWA
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onAndroidClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) { /* noop */ }
      setDeferredPrompt(null);
    } else {
      setModal("android");
    }
  };

  return (
    <div id="download" className="border-t-2 border-foreground bg-background">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Get the app</div>
          <h2 className="font-heading font-black uppercase text-3xl md:text-5xl mt-1 leading-tight">
            Install <span className="bg-foreground text-background px-2">Field Plan</span><br />on your phone.
          </h2>
          <p className="mt-5 text-muted-foreground max-w-xl">
            Field Plan installs like an app straight from your browser — no App Store, no Play Store, no waiting.
            Tap the icon on your home screen and you're onsite ready in one tap.
            {installed && <span className="ml-2 font-heading font-bold uppercase text-foreground">You're already installed ✓</span>}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="install-ios" onClick={() => setModal("ios")}
              className="btn-rugged inline-flex items-center gap-3 text-base">
              <Apple className="w-5 h-5" />
              <div className="text-left leading-tight">
                <div className="font-mono text-[9px] tracking-widest">Install on</div>
                <div className="font-heading font-black text-lg tracking-tight">iPhone / iPad</div>
              </div>
            </button>
            <button data-testid="install-android" onClick={onAndroidClick}
              className="btn-rugged inline-flex items-center gap-3 text-base">
              <Smartphone className="w-5 h-5" />
              <div className="text-left leading-tight">
                <div className="font-mono text-[9px] tracking-widest">Install on</div>
                <div className="font-heading font-black text-lg tracking-tight">Android</div>
              </div>
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <div className="flex items-center gap-2"><Home className="w-4 h-4 text-foreground" /> Home-screen icon</div>
            <div className="flex items-center gap-2"><Download className="w-4 h-4 text-foreground" /> No app store</div>
            <div className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-foreground" /> Works offline (shell)</div>
            <div className="flex items-center gap-2"><Apple className="w-4 h-4 text-foreground" /> One-tap open</div>
          </div>
        </div>

        <div className="md:col-span-5 flex flex-col items-center gap-6">
          {/* QR code panel */}
          <div className="border-2 border-foreground bg-background p-5 shadow-block-sm text-center max-w-[260px]">
            <div className="flex items-center justify-center gap-2 mb-3">
              <QrCode className="w-4 h-4" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Scan to install</div>
            </div>
            <div className="border-2 border-foreground bg-white p-3 inline-block">
              <QRCodeSVG value={INSTALL_URL} size={168} level="M" bgColor="#FFFFFF" fgColor="#050505" includeMargin={false} />
            </div>
            <div className="mt-3 font-heading font-black uppercase text-sm">Point your camera</div>
            <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground break-all">
              {INSTALL_URL.replace(/^https:\/\//, "")}
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative w-[260px] h-[520px] bg-foreground border-4 border-foreground rounded-[46px] p-3 shadow-block">
            <div className="w-full h-full bg-background rounded-[30px] overflow-hidden relative">
              <div className="absolute top-0 inset-x-0 h-6 bg-foreground rounded-b-2xl mx-16" />
              <div className="pt-10 px-5">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-[#2563EB] rounded-lg grid place-items-center font-heading font-black text-background text-sm border-2 border-foreground">FP</div>
                  <div className="leading-tight">
                    <div className="font-mono text-[8px] uppercase tracking-widest text-muted-foreground">Braeside</div>
                    <div className="font-heading font-black text-sm uppercase">Field Plan</div>
                  </div>
                </div>
                <div className="mt-4 font-heading font-black uppercase text-xl leading-tight">Sydney Job</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">7 pins · 3 runs</div>
                <div className="mt-3 h-40 border-2 border-foreground bp-hatch grid place-items-center relative overflow-hidden">
                  <div className="absolute w-3 h-3 rounded-full border-2 border-foreground bg-[#2563EB]" style={{ top: "40%", left: "35%" }} />
                  <div className="absolute w-3 h-3 rounded-full border-2 border-foreground bg-[#EAB308]" style={{ top: "60%", left: "62%" }} />
                  <div className="absolute w-3 h-3 rounded-full border-2 border-foreground bg-[#22C55E]" style={{ top: "30%", left: "70%" }} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-1.5">
                  {["#2563EB", "#DC2626", "#F59E0B", "#EAB308", "#22C55E", "#14B8A6"].map((c) => (
                    <div key={c} className="h-6 border-2 border-foreground" style={{ background: c }} />
                  ))}
                </div>
                <div className="mt-3 border-2 border-foreground p-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#FF5722]" />
                  <div className="font-mono text-[8px] uppercase tracking-widest">Onsite · Auto-saved</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* iOS instructions */}
      <Dialog open={modal === "ios"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="border-2 border-foreground rounded-none max-w-md" data-testid="install-modal-ios">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <Apple className="w-5 h-5" /> Install on iPhone / iPad
            </DialogTitle>
            <DialogDescription>
              Safari is required — Chrome on iOS cannot install web apps.
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">1</span>
              <span>Open this site in <strong>Safari</strong> on your iPhone or iPad.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">2</span>
              <span className="flex items-center gap-2">
                Tap the <Share className="w-4 h-4 inline" /> <strong>Share</strong> button at the bottom of Safari.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">3</span>
              <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">4</span>
              <span>Tap <strong>Add</strong> — Field Plan now sits on your home screen like any other app.</span>
            </li>
          </ol>
          <div className="mt-4 border-2 border-foreground bg-muted/40 p-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Tip: The icon uses the blue Field Plan "FP" tile. Open once while online so it caches the shell.
          </div>
        </DialogContent>
      </Dialog>

      {/* Android instructions */}
      <Dialog open={modal === "android"} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="border-2 border-foreground rounded-none max-w-md" data-testid="install-modal-android">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> Install on Android
            </DialogTitle>
            <DialogDescription>
              Chrome, Edge or Samsung Internet will normally show a native install prompt.
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">1</span>
              <span>Open this site in <strong>Chrome</strong> (or Edge / Samsung Internet) on your Android phone.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">2</span>
              <span className="flex items-center gap-2">
                Tap the <Menu className="w-4 h-4 inline" /> menu (three dots) in the top right.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">3</span>
              <span>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 border-2 border-foreground bg-background grid place-items-center font-heading font-black text-xs shrink-0">4</span>
              <span>Tap <strong>Install</strong> — Field Plan launches in full-screen from your app drawer.</span>
            </li>
          </ol>
          <div className="mt-4 border-2 border-foreground bg-muted/40 p-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Tip: If we detect Chrome can install, tapping the Android button prompts you directly — no menu needed.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GetTheApp;
